import {
  GetFrontRichiesteIdImmaginiIdDimensioni,
  parseApiClientFullResponse,
  useGetFrontRichiesteIdImmaginiId,
} from '@framework/api-client';
import { useEffect, useState } from 'react';
import { getFilenameFromHeaders } from '../../../../utils/download/useFileDownloadApiOptions';

export interface IImageInfo {
  url: string;
  mimeType: string;
  fileName: string;
}

export interface IPreviewSubmissionImageProps {
  submissionId: string;
  attachmentId: string;
  dimension: GetFrontRichiesteIdImmaginiIdDimensioni;
  enabled?: boolean;
}

/**
 * Custom Hook per ottenere l'anteprima di un'immagine allegata a una richiesta.
 */
export function usePreviewSubmissionImageAttachment(
  props: IPreviewSubmissionImageProps,
) {
  const { submissionId, attachmentId, dimension, enabled = true } = props;
  const [imageInfo, setImageInfo] = useState<IImageInfo | null>(null);

  const { data: imageRawResponse, isLoading } =
    useGetFrontRichiesteIdImmaginiId(
      submissionId,
      attachmentId,
      {
        dimensioni: dimension,
      },
      {
        request: {
          fullResponse: true,
          responseType: 'arraybuffer' as const,
        },
        query: {
          keepPreviousData: true,
          enabled:
            attachmentId != null &&
            !attachmentId.includes('rc-upload-') &&
            enabled,
          cacheTime: 10000,
          staleTime: 1000 * 60 * 5,
        }, // Cache per 5 minuti
      },
    );

  const imageResponse = parseApiClientFullResponse(imageRawResponse);
  const { data, headers } = imageResponse;

  useEffect(() => {
    if (!data) {
      setImageInfo(null);
      return;
    }

    const mimeType = headers?.getContentType()?.toString();
    const fileName = getFilenameFromHeaders(headers);
    const blobData = data;

    if (!blobData || !mimeType) {
      return;
    }

    const url = URL.createObjectURL(new Blob([blobData], { type: mimeType }));
    setImageInfo({ url, mimeType, fileName });

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [data, headers]);

  return { imageInfo, isLoading };
}
